import { Component, OnInit, EventEmitter, Output, OnDestroy } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { ReservationSummaryPaymentsDialogComponent } from './reservation-summary-payments-dialog/reservation-summary-payments-dialog.component';
import { ReservationService } from '../../reservation.service';

import { Payment, PaymentDetails, PaymentsTerm, Reservation, User } from '../../reservation.model';

import { DataBagService } from '@/common/data-bag.service';
import { DateService } from '@/common/date.service';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { QuestionService } from '@/common/question-dialog/question-dialog.service';
import { ReservationSummaryApiService } from '../services/reservation-summary-api.service';
import { ReservationSummaryStoreService } from '../services/reservation-summary-store.service';
import { Subject, takeUntil } from 'rxjs';
import { PaymentSource, PaymentStatus, PaymentTermStatus } from '@/common/enums/paymentStatus';
import { PaymentTermCountKind } from '@/common/enums/PaymentTermCountKind';
import { PaymentTermKind } from '@/common/enums/PaymentTermKind';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { PaymentKindId } from '@/common/enums/PaymentKind';
import { MatSnackBar } from '@angular/material/snack-bar';
import { daysDifference } from '@/_utils/days-difference';

@Component({
	selector: 'app-reservation-summary-payments',
	templateUrl: './reservation-summary-payments.component.html',
	styleUrls: ['./reservation-summary-payments.component.scss'],
})
export class ReservationSummaryPaymentsComponent implements OnInit, OnDestroy {
	@Output() resizeState = new EventEmitter<boolean>();

	reservation: Reservation;

	constructor(
		private readonly _service: ReservationService,
		private readonly api: DataBagService,
		private readonly infobox: InfoboxService,
		private readonly questionbox: QuestionService,
		private readonly dateService: DateService,
		public dialog: MatDialog,
		public reservationApiService: ReservationSummaryApiService,
		public store: ReservationSummaryStoreService,
		private readonly snackBar: MatSnackBar,
	) {}

	private paymentsTermsToDelete: number[] = [];
	paymentsTermsEdit = false;
	savingPaymentTerms = false;
	allPaymentsTerms: PaymentsTerm[] = [];
	paymentsTerms: PaymentsTerm[] = [];
	selectedPaymentTermKind: PaymentTermKind = PaymentTermKind.PAYMENT;
	selectedPaymentTermTabIndex = 0;
	paymentTermsEmptyStateMessage = 'Brak terminów płatności';
	payments: Payment[] = [];

	displayedColumns: string[] = ['date', 'condition', 'amount', 'status', 'info'];

	private readonly paymentTermColumnsWithCondition = ['date', 'condition', 'amount', 'status', 'info'];
	private readonly paymentTermColumnsWithoutCondition = ['date', 'amount', 'status', 'info'];

	displayedPaymentColumns: string[] = ['date', 'kind', 'status', 'amount'];
	matData = new MatTableDataSource<any>();
	matPaymentData = new MatTableDataSource<any>();
	isAllPaymentsShown = false;
	isPaymentsButtonVisible = false;

	private readonly destroy$: Subject<void> = new Subject();

	ngOnInit(): void {
		const { getPaymentTerm, getPayment } = this.reservationApiService.actions();
		const { paymentTerm, payment } = this.store.getters();

		this._service.reservation$.subscribe((val) => {
			this.reservation = val;
			this._service.loadPaymentKinds();
			this._service.loadIncomingBankAccounts('organization__is_oskar=true&limit=999');
			getPaymentTerm(this.reservation.id);
			getPayment(this.reservation.id);
		});

		payment()
			.pipe(takeUntil(this.destroy$))
			.subscribe((results) => {
				if (results) {
					this.convertPayments(results);
				}
			});

		paymentTerm()
			.pipe(takeUntil(this.destroy$))
			.subscribe((results) => {
				if (results) {
					this.convertPaymentsTerms(results);
				}
			});
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	convertPaymentsTerms(results: PaymentsTerm[]) {
		if (results) {
			// Keep API order (backend: term, id).
			this.allPaymentsTerms = results;
			this.allPaymentsTerms.map((term) => (term.previousValue = term.value));
			this.allPaymentsTerms = this.refactorPaymentsData(this.allPaymentsTerms) as PaymentsTerm[];
		} else {
			this.allPaymentsTerms = [];
		}

		this.updateMatData();
	}

	onPaymentTermTabChange(event: MatTabChangeEvent): void {
		const kinds = [PaymentTermKind.PAYMENT, PaymentTermKind.REFUND, PaymentTermKind.COMMISSION];

		if (this.paymentsTermsEdit) {
			this.cancelEditPaymentTerm();
		}

		this.selectedPaymentTermTabIndex = event.index;
		this.selectedPaymentTermKind = kinds[event.index];
		this.updateMatData();
	}

	private updatePaymentTermsEmptyStateMessage(): void {
		switch (this.selectedPaymentTermKind) {
			case PaymentTermKind.REFUND:
				this.paymentTermsEmptyStateMessage = 'Brak zwrotów';
				break;
			case PaymentTermKind.COMMISSION:
				this.paymentTermsEmptyStateMessage = 'Brak prowizji';
				break;
			default:
				this.paymentTermsEmptyStateMessage = 'Brak terminów płatności';
				break;
		}
	}

	private updateMatData(): void {
		this.matData.data = this.getPaymentTermsByKind(this.selectedPaymentTermKind);
		this.updateDisplayedColumns();
		this.updatePaymentTermsEmptyStateMessage();
	}

	private updateDisplayedColumns(): void {
		this.displayedColumns =
			this.selectedPaymentTermKind === PaymentTermKind.PAYMENT
				? this.paymentTermColumnsWithCondition
				: this.paymentTermColumnsWithoutCondition;
	}

	private getPaymentTermsByKind(kind: PaymentTermKind): PaymentsTerm[] {
		return this.allPaymentsTerms.filter((term) => this.getPaymentTermKind(term) === kind);
	}

	private getPaymentTermKind(term: PaymentsTerm): PaymentTermKind {
		return term.kind ?? PaymentTermKind.PAYMENT;
	}

	convertPayments(results: Payment[]) {
		if (results) {
			this.payments = results.sort(
				(a: Payment, b: Payment) => this.convertDateToNumber(a.posting) - this.convertDateToNumber(b.posting),
			);
		} else this.payments = [];

		const matPaymentTableData: Payment[] = this.initialFilterPayments();
		if (matPaymentTableData && matPaymentTableData.length) {
			this.matPaymentData.data = matPaymentTableData.filter(
				(payment) => payment.status === PaymentStatus.COMPLETED,
			);
			this.isPaymentsButtonVisible = matPaymentTableData.length > this.matPaymentData.data.length;
		} else {
			this.matPaymentData.data = [];
		}
	}

	private refactorPaymentsData(payments: (Payment | PaymentsTerm)[]): (Payment | PaymentsTerm)[] {
		return payments.map((singlePayment: Payment | PaymentsTerm) => {
			const user: User = singlePayment.modifiedBy
				? singlePayment.modifiedBy
				: singlePayment.createdBy
					? singlePayment.createdBy
					: null;

			singlePayment['_user'] = this.getFullNameInfo(user);
			return singlePayment;
		});
	}
	private findNewestInProgressPaymentIndex(): number {
		const in_progress = [...this.payments]
			.reverse()
			.filter((payment) => payment.status === PaymentStatus.IN_PROGRESS)[0];

		return in_progress ? this.payments.findIndex((payment) => payment.id === in_progress.id) : -1;
	}

	openDialog() {
		const dialogRef = this.dialog
			.open(ReservationSummaryPaymentsDialogComponent, {
				data: {
					data: null,
					reservationId: this.reservation.id,
				},
			})
			.afterClosed()
			.subscribe(() => {
				this.reservationApiService.actions().getReservation(this.reservation.id);
				dialogRef.unsubscribe();
			});
	}

	private convertDateToNumber(date: Date) {
		return this._service.convertDateToNumber(date);
	}

	getUnit(payment_term: PaymentsTerm) {
		return this._service.getUnit(payment_term);
	}

	setEditPaymentTerm(): void {
		this.paymentsTermsEdit = true;

		this.paymentsTerms = this.getPaymentTermsByKind(PaymentTermKind.PAYMENT).map((paymentTerm) => {
			const reservationTime = new Date(this.reservation.time.lower);
			const paymentTermDate = new Date(paymentTerm.term);
			const days = daysDifference(paymentTermDate, reservationTime);

			return {
				...paymentTerm,
				days,
			};
		});

		const valuesBefore = this.paymentsTerms.map((t) => t.value);

		this.paymentsTerms.forEach((term) => {
			if (term.countKind) {
				this.calculateTermValue(term);
			}
		});

		const hasChanges = this.paymentsTerms.some((t, i) => t.value !== valuesBefore[i]);
		if (hasChanges) {
			this.snackBar.open('Wartości terminów płatności zostały przeliczone', 'OK', { duration: 3000 });
		}

		this.resizeState.emit(true);
	}

	cancelEditPaymentTerm() {
		const { getPaymentTerm } = this.reservationApiService.actions();
		this.paymentsTermsToDelete = [];
		this.paymentsTermsEdit = false;
		getPaymentTerm(this.reservation.id);
		this.resizeState.emit(false);
	}

	addNewPaymentTerm() {
		this.paymentsTerms = [
			...this.paymentsTerms,
			{
				id: null,
				term: null,
				value: null,
				countValue: null,
				countKind: null,
				kind: PaymentTermKind.PAYMENT,
				currency: this.reservation.currency.id,
				status: PaymentTermStatus.UNSETTLED,
				previousValue: null,
				createdBy: null,
				modifiedBy: null,
			},
		];
	}

	recalculateTermValue(term?) {
		this.paymentsTerms.map((v) => {
			if (this.paymentsTerms.indexOf(v) > this.paymentsTerms.indexOf(term) && v.countKind === 'R' && term !== v) {
				this.calculateTermValue(v);
			}
		});
	}

	removePaymentTerm(term) {
		this.paymentsTerms = this.paymentsTerms.filter((v) => v !== term);
		this.recalculateTermValue();
		if (term.id) this.paymentsTermsToDelete.push(term.id);
	}

	convertToGrosze(price) {
		return Math.ceil(price * 100);
	}

	convertToPermill(value) {
		return value * 10;
	}

	calculateTermValue(term: PaymentsTerm) {
		const reservationPrice = this._service.totalPrice;
		switch (term.countKind) {
			case 'P':
				term.value = Math.round(reservationPrice * (term.countValue / 1000));
				this.recalculateTermValue();
				break;
			case 'V':
				term.value = term.countValue < 0 ? 0 : term.countValue;
				this.recalculateTermValue();
				break;
			case 'R':
				if (term.countValue === null) break;

				const summedValue = this.paymentsTerms
					.slice(0, this.paymentsTerms.indexOf(term))
					.reduce((acc, currentTerm) => acc + currentTerm.value, 0);

				const amount = Math.round(reservationPrice * (term.countValue / 1000) - summedValue);

				term.value = amount <= 0 ? 0 : amount;

				this.recalculateTermValue(term);
				break;
			default:
				break;
		}
	}

	private checkPrices(payload) {
		const paymentsTermSum = payload.reduce((previousValue, currentValue) => {
			return previousValue + currentValue.value;
		}, 0);

		const reservationTotalPrice = this._service.totalPrice;
		return paymentsTermSum < reservationTotalPrice;
	}

	async savePaymentsTerms() {
		if (this.paymentsTerms.some((v) => !v.term || v.countValue === null || v.value === null)) {
			return this.infobox.error('Wypełnij wszystkie wymagane pola');
		}

		if (this.paymentsTerms.some((v) => v.countValue < 0)) {
			return this.infobox.error('Kwota nie może być ujemna!');
		}

		const payload = this.paymentsTerms.map((v) => {
			return {
				...v,
				kind: PaymentTermKind.PAYMENT,
				table_name: 'reservation_reservation',
				source: PaymentSource.MANUAL,
				term: this.dateService.formatDateToGivenLocale(v.term, 'sv-SE'),
				table_id: this.reservation.id,
				currency: this.reservation.currency.id,
			};
		});

		if (this.checkPrices(payload)) {
			return this.infobox.error(`Suma wartości terminów płatności niezgodne z wartością rezerwacji.`);
		}

		this.savingPaymentTerms = true;

		const deletePromises = this.paymentsTermsToDelete.map(
			(id) =>
				new Promise((resolve, reject) => {
					this.api.del(`/payment/term/${id}/`, (result, { error }) => {
						if (error) reject(error);
						else resolve(result);
					});
				}),
		);

		await Promise.all(deletePromises);

		const paymentTermsToCreate = payload.filter((v) => !v.id);

		if (paymentTermsToCreate.length > 0) {
			await new Promise((resolve, reject) => {
				this.api.post('/payment/term/', paymentTermsToCreate, (result: PaymentsTerm, { error }) => {
					if (error) reject(error);
					else resolve(result);
				});
			});
		}

		const paymentTermsToPatch = payload.filter((v) => v.id);

		if (paymentTermsToPatch.length > 0) {
			await new Promise((resolve, reject) => {
				this.api.patch(`/payment/term/`, paymentTermsToPatch, (result: PaymentsTerm[], { error }) => {
					if (error) reject(error);
					else resolve(result);
				});
			});
		}

		const { getPaymentTerm } = this.reservationApiService.actions();

		getPaymentTerm(this.reservation.id);

		this.paymentsTermsToDelete = [];

		this.paymentsTermsEdit = false;
		this.resizeState.emit(false);

		this.savingPaymentTerms = false;
		this.infobox.success('Pomyślnie zapisano!');
		await this.reservationApiService.actions().getReservation(this.reservation.id);
	}

	openPaymentDetails(payment: PaymentDetails, paymentConfirm: boolean) {
		const { getPayment } = this.reservationApiService.actions();
		this.api.get(`/payment/${payment.id}/`, (result, { error }) => {
			if (result) {
				const dialogRef = this.dialog.open(ReservationSummaryPaymentsDialogComponent, {
					data: {
						data: result,
						generatePaymentConfirm: paymentConfirm,
					},
				});
				dialogRef.afterClosed().subscribe((result) => {
					if (result) getPayment(this.reservation.id);
				});
			}
		});
	}

	deletePayment(term_id: number) {
		this.questionbox.ask('Czy napewno chcesz usunąć tę płatność?', 'deleteAsk');
		const { getPayment, getPaymentTerm } = this.reservationApiService.actions();
		const questionSubscription = this.questionbox.status.subscribe((result) => {
			if (!result.result) return;

			questionSubscription.unsubscribe();
			if (result.dialogId != 'deleteAsk') return;

			this.api.del(`/payment/${term_id}/`, (result, { error }) => {
				this.infobox.success('Pomyślnie usunięto płatność');
				getPayment(this.reservation.id);
				getPaymentTerm(this.reservation.id);
			});
		});
	}

	calculateTermDate(event, term: PaymentsTerm) {
		const reservationStartDate = this.reservation.time.lower;

		const offset = event.target.value;

		term.term = this.dateService.addDays(-offset, reservationStartDate);
	}

	convertPriceByKind(value: number, kind: string): number {
		if (kind === 'V') return value * 10;
		return value / 10;
	}

	/**
	 * Determines whether a payment is not deletable for business reasons.
	 * @param payment {Payment} - target payment to check its kind.
	 * @returns {boolean} - True if the payment is of TPay or PayU kinds and is not deletable; otherwise, false.
	 */
	isNotDeletablePayment(payment: Payment): boolean {
		return [PaymentKindId.TPAY, PaymentKindId.PAYU].includes(payment.kind.id);
	}

	showAllPayments() {
		this.isAllPaymentsShown = !this.isAllPaymentsShown;
		const matPaymentTableData: Payment[] = this.initialFilterPayments();
		this.matPaymentData.data = matPaymentTableData.filter((payment) =>
			this.isAllPaymentsShown ? payment : payment.status === PaymentStatus.COMPLETED,
		);
	}

	private initialFilterPayments(): Payment[] {
		const newestInProgressIndex = this.findNewestInProgressPaymentIndex();
		this.payments = this.refactorPaymentsData(this.payments) as Payment[];
		return this.payments.filter(
			(payment) =>
				payment.status !== PaymentStatus.IN_PROGRESS || payment === this.payments[newestInProgressIndex],
		);
	}

	private getFullNameInfo(user: User): string {
		if (!user) {
			return `Ostatnia modyfikacja : System`;
		}

		return user.email === 'www@oskar.com.pl'
			? `Ostatnia modyfikacja : System`
			: user.firstName && user.lastName
				? `Ostatnia modyfikacja : ${user.firstName} ${user.lastName}`
				: `Ostatnia modyfikacja : ${user.email}`;
	}

	protected readonly PaymentTermStatus = PaymentTermStatus;
	protected readonly PaymentStatus = PaymentStatus;
	protected readonly PaymentTermCountKind = PaymentTermCountKind;
	protected readonly PaymentTermKind = PaymentTermKind;
	protected readonly PaymentKindId = PaymentKindId;
}
